import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { IconLoader, IconX } from "./Icons";
import { AUTO_READ_KEYS, PERMISSION_DEFS, PermLevel, normalizePerms } from "../permissions";

interface Props {
  user: { id: string; email: string; full_name: string | null; permissions?: Record<string, unknown> };
  onSaved: () => void;
  onClose: () => void;
}

export default function PermissionsModal({ user, onSaved, onClose }: Props) {
  const { t } = useTranslation();
  const { data: tenantProfile } = useQuery({
    queryKey: ["tenantProfile"],
    queryFn: () => api.get<{ eet_mode?: string }>("/tenant/profile").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const eetEnabled = !!tenantProfile && tenantProfile.eet_mode !== "disabled";
  const defs = PERMISSION_DEFS.filter((d) => !d.eetOnly || eetEnabled);
  const [state, setState] = useState<Record<string, PermLevel | boolean>>(() => normalizePerms(user.permissions));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allowed = (req: { key: string; level?: "read" | "write" }) => {
    const v = state[req.key];
    if (req.level === "write") return v === "write";
    return v === "read" || v === "write" || v === true;
  };

  const setValue = (key: string, value: PermLevel | boolean) => {
    setState((s) => {
      const next = { ...s, [key]: value };
      // Clear dependents whose prerequisite disappeared.
      for (const def of PERMISSION_DEFS) {
        if (!def.requires) continue;
        const reqVal = next[def.requires.key];
        const okReq = def.requires.level === "write" ? reqVal === "write" : (reqVal === "read" || reqVal === "write" || reqVal === true);
        if (!okReq) next[def.key] = def.type === "level" ? "off" : false;
      }
      return next;
    });
  };

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api.put(`/tenant/users/${user.id}`, { permissions: state });
      onSaved();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("permModal.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", padding: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600 }}>{t("permModal.title")}</h2>
            <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{user.full_name || user.email}</p>
          </div>
          <button className="ghost" onClick={onClose}><IconX size={18} /></button>
        </div>

        {err && <div style={{ marginBottom: 12, color: "var(--red)", fontSize: 13 }}>{err}</div>}

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {defs.map((def) => {
              const disabled = def.requires ? !allowed(def.requires) : false;
              return (
                <tr key={def.key}>
                  <td style={{ padding: "8px 12px 8px 0", fontSize: 14, color: disabled ? "var(--muted)" : "var(--text)" }}>
                    {t(def.label)}
                    {def.requires && disabled && (
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {t("permModal.requires")}: {t(PERMISSION_DEFS.find((d) => d.key === def.requires!.key)?.label ?? "")}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px 0", textAlign: "right", whiteSpace: "nowrap" }}>
                    {def.type === "level" ? (
                      <select
                        value={state[def.key] as string}
                        disabled={disabled}
                        onChange={(e) => setValue(def.key, e.target.value as PermLevel)}
                        style={{ fontSize: 13, padding: "4px 8px" }}
                      >
                        {!AUTO_READ_KEYS.has(def.key) && <option value="off">{t("permModal.off")}</option>}
                        <option value="read">{t("permModal.read")}</option>
                        <option value="write">{t("permModal.write")}</option>
                      </select>
                    ) : (
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer" }}>
                        <input
                          type="checkbox"
                          checked={state[def.key] === true}
                          disabled={disabled}
                          onChange={(e) => setValue(def.key, e.target.checked)}
                        />
                        {t("permModal.allow")}
                      </label>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
          <button className="ghost" onClick={onClose} disabled={saving}>{t("common.cancel")}</button>
          <button className="primary" onClick={() => void save()} disabled={saving}>
            {saving ? <IconLoader size={18} /> : t("permModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
